import { useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useMe } from "../api/me";
import type { AssetDetail } from "../api/types";
import type { ViewerHandle } from "../viewer/Viewer";
import { ColourControl } from "./ColourControl";
import {
  INSPECTOR_GRID,
  Inspector,
  InspectorHeader,
  TryOnCards,
  UsedInAds,
  Viewer,
  ViewerSlot,
} from "./inspector";
import { TrueRenderButton } from "./TrueRenderButton";

const NO_POSES = {};

function MaterialInspector(props: { detail: AssetDetail; onExpired(): void }) {
  const { detail } = props;
  const [tint, setTint] = useState<string | undefined>(undefined);
  const subject = useMemo(
    () => ({ kind: "material" as const, detail, tint }),
    [detail, tint],
  );
  const tryOn = (detail.try_on ?? []).slice(0, 8);
  const viewerRef = useRef<ViewerHandle>(null);
  const stills = useMe().data?.features?.stills === true;
  const target = useMemo(
    () => ({ kind: "material" as const, id: detail.id }),
    [detail.id],
  );
  // A surface's true render takes only its tint.
  const dressing = useMemo(
    () => ({
      materials: {},
      skins: {},
      colors_hex: tint ? { surface: tint } : ({} as Record<string, string>),
    }),
    [tint],
  );
  return (
    <>
      <InspectorHeader detail={detail} />
      <div className={INSPECTOR_GRID}>
        <ViewerSlot>
          <Viewer
            ref={viewerRef}
            subject={subject}
            onExpired={props.onExpired}
          />
        </ViewerSlot>
        <div className="flex flex-col gap-6">
          {detail.meta.tint !== "none" ? (
            <ColourControl value={tint ?? null} onChange={setTint} />
          ) : null}
          {stills ? (
            <TrueRenderButton
              target={target}
              dressing={dressing}
              poses={NO_POSES}
              viewerRef={viewerRef}
            />
          ) : null}
          <section className="flex flex-col gap-3" data-testid="where-to-use">
            <p className="m-0 text-sm text-slate-700">
              Surfaces are chosen for you when the ad is written. To ask for
              this one, name it in your brief
              {tryOn.length > 0 ? ", or put it on an object:" : "."}
            </p>
            {tryOn.length > 0 ? (
              <TryOnCards
                items={tryOn}
                to={(item) =>
                  `/assets/shape/${item.id}?material=${item.slot}:${detail.id}`
                }
              />
            ) : null}
          </section>
        </div>
      </div>
      <UsedInAds detail={detail} />
    </>
  );
}

/** `/assets/material/:id`: a surface on a sphere and a cube, in a colour of
 *  the user's when it can take one, and the objects it can be put on. */
export function AssetMaterialPage() {
  const { id = "" } = useParams();
  return (
    <Inspector kind="material" id={id}>
      {(detail, asset) => (
        <MaterialInspector
          key={detail.id}
          detail={detail}
          onExpired={asset.onExpired}
        />
      )}
    </Inspector>
  );
}
